import React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";
import { FETCH_PROJECT_QUERY } from "../../../../../constants/graphql/queries";
import { fullWidthDialog } from "../../../../../constants/dialog";
import useWizardStep from "../../../../../hooks/useWizardStep";
import Details from "./Details/Details";
import Items from "./Items/Items";
import AncillaryCharges from "./AncillaryCharges/AncillaryCharges";
import Confirmation from "./Confirmation/Confirmation";
import IntegrationCounterpartStep from "src/components/integrations/IntegrationCounterpartStep";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import { withIntegrationStep, wizardProgress } from "src/utils/integrationWizardSteps";
import { useNavigate } from "react-router";

const BASE_STEPS = ["details", "items", "charges", "confirmation"];

export default function InternalWorkOrderGuidedWizardModal({ internalWorkOrder, onToggle }) {
  const navigate = useNavigate();
  const { data: { project = {} } = {}, loading: loadingProject } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: internalWorkOrder?.project?.id },
    skip: !internalWorkOrder?.project?.id,
  });
  const { hasIntegration, loading: loadingIntegration } = useHasEnabledIntegration(project);
  const STEPS = withIntegrationStep(BASE_STEPS, hasIntegration);
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");

  const handleClose = () => {
    onToggle();
  };

  const loadingInternalWorkOrder = !internalWorkOrder || !internalWorkOrder.id;
  const loading = loadingProject || loadingInternalWorkOrder || loadingIntegration;
  const step = useWizardStep(STEPS, { skip: loading });

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose} sx={fullWidthDialog}>
      <Box display="flex" flexDirection="column" height="100%">
        {loading ? (
          <Box p={3} display="flex" minHeight={400} alignItems="center" justifyContent="center">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box minHeight={400} height="100%">
            {step === "details" && (
              <Details project={project} internalWorkOrder={internalWorkOrder} />
            )}
            {step === "items" && <Items internalWorkOrder={internalWorkOrder} project={project} />}
            {step === "integration" && (
              <IntegrationCounterpartStep
                title="Link Moveware Diary Action"
                info={internalWorkOrder.internalWorkOrderNumber}
                project={project}
                linkableType="InternalWorkOrder"
                linkableId={internalWorkOrder?.id}
                date={internalWorkOrder?.deadlineDate}
                {...wizardProgress(STEPS, "integration")}
                startTime={internalWorkOrder?.pickingStartTime}
                completeTime={internalWorkOrder?.pickingCompleteTime}
                onBack={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set("step", "items");
                  navigate({ search: params.toString() });
                }}
                onContinue={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set("step", hasAdditionalCharges ? "charges" : "confirmation");
                  navigate({ search: params.toString() });
                }}
              />
            )}
            {step === "charges" && (
              <AncillaryCharges project={project} internalWorkOrder={internalWorkOrder} />
            )}
            {step === "confirmation" && (
              <Confirmation internalWorkOrder={internalWorkOrder} onClose={handleClose} />
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
